/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { format, sub, add, addMinutes, startOfWeek, isBefore, startOfDay } from 'date-fns';

import { FinancialConfig } from '@futrli-sdk/predict';

import {
  COMPARISON_OPTIONS,
  CalendarOptions,
  PERIOD_END_OPTIONS,
  PERIOD_OPTIONS,
  PeriodEndOption,
  PeriodType
} from './types';
import { DATE_FORMAT, DEFAULT_PERIOD_NUMBER } from './constants';
import { forceLocalDate } from '../utils/dates';

/**
 * get current quarter date string from date and org's financial config
 */
export const getSelectedQuarter = (date: string, financialConfig: FinancialConfig) => {
  const quarterOptions = getQuarterOptions(financialConfig);
  const currentDate = new Date(date);
  const currentQuarter = quarterOptions.find(
    o => currentDate.getTime() >= o.getTime() && currentDate.getTime() < add(o, { months: 3 }).getTime()
  );
  if (currentQuarter) {
    return format(currentQuarter, 'yyyy-MM-dd');
  }
  return '';
};

/**
 * checks if a date is before current period (current day, month, week, quarter or year)
 */
export const isBeforeCurrentPeriod = (date: string, type: PeriodType) => {
  if (type === 'daily') {
    return isBefore(new Date(date), add(new Date(), { days: -1 }));
  }
  if (type === 'monthly') {
    return isBefore(add(new Date(date), { months: 1 }), add(new Date(), { days: -1 }));
  }
  if (type === 'weekly_sun' || type === 'weekly_mon') {
    return isBefore(add(new Date(date), { weeks: 1 }), add(new Date(), { days: -1 }));
  }
  if (type === 'quarterly') {
    return isBefore(add(new Date(date), { months: 3 }), add(new Date(), { days: -1 }));
  }
  if (type === 'yearly') {
    return isBefore(add(new Date(date), { years: 1 }), add(new Date(), { days: -1 }));
  }
};

/**
 * checks if a date is before current period (current day, month, week, quarter or year)
 */
export const isBeforeCurrentPeriodDate = (date: string, dateToCompare: string, type: PeriodType) => {
  if (type === 'daily') {
    return isBefore(startOfDay(new Date(date)), startOfDay(new Date(dateToCompare)));
  }
  if (type === 'monthly') {
    return isBefore(add(add(new Date(date), { months: 1 }), { days: -1 }), new Date(dateToCompare));
  }
  if (type === 'weekly_sun' || type === 'weekly_mon') {
    return isBefore(add(add(new Date(date), { weeks: 1 }), { days: -1 }), new Date(dateToCompare));
  }
  if (type === 'quarterly') {
    return isBefore(add(add(new Date(date), { months: 3 }), { days: -1 }), new Date(dateToCompare));
  }
  if (type === 'yearly') {
    return isBefore(add(add(new Date(date), { years: 1 }), { days: -1 }), new Date(dateToCompare));
  }
};

/**
 * returns period type where all weekly options are just called weekly
 */
export const getPeriodType = (ref?: string) => (ref === 'weekly_mon' || ref === 'weekly_sun' ? 'weekly' : ref);

/**
 * returns offset from weekly_sun period type
 */
export const getPeriodOffset = (ref?: string) => (!ref || ref !== 'weekly_sun' ? 0 : 6);

/**
 * get calendar year options
 */
export const getYearOptions = () => {
  const currentYear = new Date().getFullYear();

  return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
};

/**
 * get calendar quarter options
 */
export const getQuarterOptions = (financialConfig?: FinancialConfig) => {
  if (!financialConfig) {
    return [];
  }

  const financialYearStart = forceLocalDate(financialConfig.current_year.start);

  const quarters = [
    sub(financialYearStart, { months: 24 }),
    sub(financialYearStart, { months: 21 }),
    sub(financialYearStart, { months: 18 }),
    sub(financialYearStart, { months: 15 }),
    sub(financialYearStart, { months: 12 }),
    sub(financialYearStart, { months: 9 }),
    sub(financialYearStart, { months: 6 }),
    sub(financialYearStart, { months: 3 }),
    financialYearStart,
    add(financialYearStart, { months: 3 }),
    add(financialYearStart, { months: 6 }),
    add(financialYearStart, { months: 9 }),
    add(financialYearStart, { months: 12 }),
    add(financialYearStart, { months: 15 }),
    add(financialYearStart, { months: 18 }),
    add(financialYearStart, { months: 21 }),
    add(financialYearStart, { months: 24 }),
    add(financialYearStart, { months: 27 }),
    add(financialYearStart, { months: 30 }),
    add(financialYearStart, { months: 33 }),
    add(financialYearStart, { months: 36 }),
    add(financialYearStart, { months: 39 }),
    add(financialYearStart, { months: 42 })
  ];
  return quarters;
};

/**
 * StartMonth-EndMonth endMonthYear -> Dec-Mar 23
 */
export const formatQuarterDateStr = (date: Date) => {
  const startMonth = format(date, 'LLL');
  const endDate = sub(add(date, { months: 3 }), { days: 1 });
  const endMonth = format(endDate, 'LLL');
  return `${startMonth}-${endMonth} ${format(endDate, 'yy')}`;
};

/**
 * returns current quarter for organization
 */
export const getCurrentQuarter = (financialConfig?: FinancialConfig) => {
  const quarterOptions = getQuarterOptions(financialConfig);

  const currentDate = new Date();

  const currentQuarter = quarterOptions.find(
    o => currentDate.getTime() >= o.getTime() && currentDate.getTime() < add(o, { months: 3 }).getTime()
  );

  return currentQuarter || new Date();
};

/**
 * get start date to display in widget
 */
export const getStartDate = (reportDate: string, type: PeriodType, financialConfig?: FinancialConfig) => {
  if (reportDate === PERIOD_OPTIONS.THIS_PERIOD) {
    if (type === 'daily') {
      return format(new Date(), DATE_FORMAT);
    }
    if (type === 'weekly_mon') {
      const startOfTheWeek = add(startOfWeek(new Date(), { weekStartsOn: 0 }), { days: 1 });
      return format(startOfTheWeek, DATE_FORMAT);
    }
    if (type === 'weekly_sun') {
      const startOfTheWeek = add(startOfWeek(new Date(), { weekStartsOn: 6 }), { days: 1 });
      return format(startOfTheWeek, DATE_FORMAT);
    }
    if (type === 'monthly') {
      const dateString = format(new Date(), DATE_FORMAT);
      return `${dateString.substring(0, dateString.lastIndexOf('-'))}-01`;
    }
    if (type === 'quarterly') {
      const currentQuarter = getCurrentQuarter(financialConfig);
      return format(currentQuarter, DATE_FORMAT);
    }
    if (type === 'yearly') {
      if (financialConfig?.current_year) {
        return financialConfig.current_year.end;
      } else {
        const year = new Date().getFullYear();
        return `${year}-12-31`;
      }
    }
  }
  if (reportDate === PERIOD_OPTIONS.LAST_PERIOD) {
    if (type === 'daily') {
      return format(add(new Date(), { days: -1 }), DATE_FORMAT);
    }
    if (type === 'weekly_mon') {
      const startOfTheWeek = add(startOfWeek(new Date(), { weekStartsOn: 0 }), { days: 1 });
      return format(add(startOfTheWeek, { days: -7 }), DATE_FORMAT);
    }
    if (type === 'weekly_sun') {
      const startOfTheWeek = add(startOfWeek(new Date(), { weekStartsOn: 6 }), { days: 1 });
      return format(add(startOfTheWeek, { days: -7 }), DATE_FORMAT);
    }
    if (type === 'monthly') {
      const dateString = format(add(new Date(), { months: -1 }), DATE_FORMAT);
      return `${dateString.substring(0, dateString.lastIndexOf('-'))}-01`;
    }
    if (type === 'quarterly') {
      const currentQuarter = getCurrentQuarter(financialConfig);
      const lastQuarter = add(currentQuarter, { months: -3 });
      return format(lastQuarter, DATE_FORMAT);
    }
    if (type === 'yearly') {
      const year = new Date(financialConfig?.current_year.end || '').getFullYear() - 1;
      const finYearEnd = financialConfig?.current_year.end || '-12-31';
      const dateMonth = finYearEnd.substring(finYearEnd.indexOf('-') + 1);
      return `${year}-${dateMonth}`;
    }
  }
  if (type === 'monthly') {
    return `${reportDate.substring(0, reportDate.lastIndexOf('-'))}-01`;
  }
  return reportDate;
};

/**
 * Returns the offset between supplied timezone and the system one
 * @param timeZone
 * @param date
 * @returns timezone offset in minutes
 */
const getOffsetFromTZString = (timeZone = 'UTC', date = new Date()) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return (utcDate.getTime() - tzDate.getTime()) / 6e4;
};

/**
 * replaces placeholders with their respective values
 * supports: @REPORT_DATE, will be replaced by current time adjusted to supplied timezone
 */
export const parseReportText = (text: string, timezone: string | undefined) => {
  let date = new Date();
  const ctz = new Date().getTimezoneOffset();
  const reportTZ = isNaN(parseInt(timezone || '0', 10)) ? getOffsetFromTZString(timezone) : +(timezone || 0);
  if (timezone?.length && reportTZ !== ctz) {
    date = addMinutes(date, ctz - reportTZ);
  }
  return text.replace(/@REPORT_DATE/g, `<span data-ph="REPORT_DATE">${format(date, 'do MMM y, h:mm aaa')}</span>`);
};

/**
 * Returns updated CalendarOptions object depending on the selected Period End option
 * @returns CalendarOptions
 */
export const getCalendarOptionsFromEndDateOption = (
  option: PeriodEndOption,
  date: string,
  calOptions: CalendarOptions | undefined
) => {
  if (option.value === PERIOD_END_OPTIONS.CUSTOM_DATES) {
    return calOptions;
  }
  const newCalOptions: CalendarOptions = calOptions
    ? { ...calOptions }
    : { type: 'monthly', periods: 3, startDate: 'this_period', comparision: COMPARISON_OPTIONS.PREVIOUS };
  if (
    [
      PERIOD_END_OPTIONS.THIS_WEEK_MON,
      PERIOD_END_OPTIONS.LAST_WEEK_MON,
      PERIOD_END_OPTIONS.THIS_WEEK_SUN,
      PERIOD_END_OPTIONS.LAST_WEEK_SUN,
      PERIOD_END_OPTIONS.THIS_MONTH,
      PERIOD_END_OPTIONS.LAST_MONTH,
      PERIOD_END_OPTIONS.THIS_QUARTER,
      PERIOD_END_OPTIONS.LAST_QUARTER,
      PERIOD_END_OPTIONS.THIS_FIN_YEAR,
      PERIOD_END_OPTIONS.LAST_FIN_YEAR,
      PERIOD_END_OPTIONS.YESTERDAY,
      PERIOD_END_OPTIONS.TODAY
    ].includes(option.value)
  ) {
    newCalOptions.type = option.type;
    newCalOptions.startDate = option.start;
  }
  if (option.value === PERIOD_END_OPTIONS.CUSTOM && date) {
    newCalOptions.type = option.type;
    newCalOptions.startDate = date;
  }
  return newCalOptions;
};

export const getPeriodsNumberFromCalendarOptions = (calOptions?: CalendarOptions) => {
  if (typeof calOptions?.periods === 'number') {
    return calOptions.periods;
  }

  return DEFAULT_PERIOD_NUMBER;
};
